import type { ChartModel, PlotKind } from './types';
import {
  bindHighchartsResetZoom,
  eventAwareTrendTooltipPositioner,
  highchartsNavigationTheme,
  highchartsResetZoomButtonOptions,
  installFloatingHighchartsExportMenu,
  makeEventMarkersKeyboardAccessible,
  timestampIsInTrendDataGap,
  trendDataGapRanges,
  type TrendDataGapRange
} from './highcharts-interactions';
import {
  genericTooltipFormatter,
  heatmapTooltipFormatter,
  highchartsDiscontinuityAxisOptions,
  highchartsPolarAxisOptions,
  highchartsSubtitleOptions,
  highchartsTitleOptions,
  highchartsTooltipTheme,
  highchartsVerticalAxisTitleOptions,
  polarTooltipFormatter,
  timeFrequencyTooltipFormatter
} from './highcharts-formatting';
import { ensureHighcharts } from './highcharts-runtime';

export {
  bindHighchartsResetZoom,
  eventAwareTrendTooltipPositioner,
  eventMarkerAriaLabel,
  floatHighchartsExportMenu,
  highchartsExportMenuViewportPosition,
  highchartsNavigationTheme,
  highchartsResetZoomButtonOptions,
  makeEventMarkersKeyboardAccessible,
  timestampIsInTrendDataGap,
  trendDataGapRanges
} from './highcharts-interactions';
export type { TrendDataGapRange } from './highcharts-interactions';
export {
  formatGenericTooltip,
  formatHeatmapTooltip,
  formatHeatmapUtcDate,
  formatPolarAxisHour,
  formatPolarHour,
  formatPolarRadialValue,
  formatPolarTooltip,
  formatTimeFrequencyTooltip,
  highchartsDiscontinuityAxisOptions,
  highchartsPolarAxisOptions,
  highchartsSubtitleOptions,
  highchartsTitleOptions,
  highchartsTooltipTheme,
  highchartsVerticalAxisTitleOptions
} from './highcharts-formatting';
export type { GenericTooltipContext, HeatmapTooltipContext, PolarTooltipContext } from './highcharts-formatting';
export { formatEventDuration, formatTooltipNumber, formatTooltipValue } from './tooltip-format';

interface HighchartsAxis {
  addPlotLine(options: Record<string, unknown>): HighchartsPlotLine | undefined;
  getExtremes(): { min?: number; max?: number };
  toValue(pixel: number, paneCoordinates?: boolean): number;
  removePlotLine(id: string): void;
  setExtremes(min: number, max: number, redraw?: boolean, animation?: boolean, eventArguments?: Record<string, unknown>): void;
}

interface HighchartsPlotLine {
  svgElem?: { element?: SVGElement };
}

interface HighchartsChart {
  container?: HTMLElement;
  contextMenu?: (...args: unknown[]) => void;
  destroy(): void;
  exportSVGElements?: HighchartsControlElement[];
  inverted?: boolean;
  openMenu?: boolean;
  plotLeft?: number;
  plotHeight?: number;
  plotTop?: number;
  plotWidth?: number;
  pointer?: { reset(allowMove?: boolean, delay?: number): void };
  reflow(): void;
  resetZoomButton?: HighchartsControlElement;
  showResetZoom?: () => void;
  xAxis?: HighchartsAxis[];
  series?: HighchartsSeries[];
  _pqEventTooltipPoint?: HighchartsPoint;
}

interface HighchartsControlElement {
  element?: Element;
  on?: (eventType: string, handler: (event?: Event) => void) => HighchartsControlElement;
  setState?: (state: number) => void;
}

interface HighchartsSeries {
  chart?: HighchartsChart;
  color?: string;
  name?: string;
  options?: { tooltip?: { valueSuffix?: string } };
  points?: HighchartsPoint[];
  visible?: boolean;
  xAxis?: { left?: number };
  yAxis?: { top?: number };
}

interface HighchartsPoint {
  color?: string;
  x?: number;
  y?: number;
  duration_ms?: number;
  duration_display?: string;
  classification?: string;
  classification_suffix?: string;
  selected?: boolean;
  options?: {
    x?: number;
    y?: number;
    duration_ms?: number;
    duration_display?: string;
    classification?: string;
    classification_suffix?: string;
  };
  plotX?: number;
  plotY?: number;
  series?: HighchartsSeries;
  graphic?: { element?: SVGElement };
  firePointEvent?: (eventType: string, event?: Event) => void;
  select?: (selected?: boolean, accumulate?: boolean) => void;
}

export interface ChartAdapterOptions {
  model: ChartModel;
  kind: PlotKind;
  cursor?: number;
  showTitle?: boolean;
  showSubtitle?: boolean;
  onRangeChange?: (range: [number, number]) => void;
  onCursorPreview?: (cursor: number) => void;
  onResetZoom?: () => void;
  resetZoomVisible?: boolean;
  zoomEnabled?: boolean;
}

export class ChartAdapter {
  private chart: HighchartsChart | null = null;
  private xAxisType: ChartModel['xAxisType'] = 'linear';
  private cursorColour = '#d9534f';
  private selectedEventChart: HighchartsChart | null = null;
  private selectedEventTimestamp: number | undefined;
  private removeFloatingMenu: (() => void) | null = null;
  private gapMousemoveTarget: HTMLElement | null = null;
  private gapMousemoveListener: ((event: MouseEvent) => void) | null = null;

  constructor(private readonly container: HTMLElement) {}

  async render(options: ChartAdapterOptions, shouldRender: () => boolean = () => true): Promise<void> {
    this.destroy();
    const highcharts = await ensureHighcharts(options.kind, options.model);
    if (!shouldRender()) return;
    const dark = document.documentElement.dataset.theme === 'dark';
    this.cursorColour = getComputedStyle(this.container).getPropertyValue('--cursor-highlight').trim() || this.cursorColour;
    highcharts.setOptions({ global: { useUTC: true }, lang: { noData: options.model.emptyMessage ?? 'No data to display' } });
    const model = options.model;
    const stableEventLegend = model.stableEventLegend === true;
    const hasSecondaryAxis = model.series.some((series) => series.yAxis === 1);
    const trendGaps = trendDataGapRanges(model);
    const polarAxes = model.polar ? highchartsPolarAxisOptions(dark, model.valueSuffix) : null;
    const discontinuityAxis = model.xAxisDiscontinuity ? highchartsDiscontinuityAxisOptions(model.xAxisDiscontinuity, dark) : null;
    this.xAxisType = model.xAxisType;
    const xAxis: Record<string, unknown> = {
      type: model.xAxisType ?? 'linear',
      title: model.inverted
        ? highchartsVerticalAxisTitleOptions(model.xAxisTitle ?? null, dark)
        : { text: model.xAxisTitle ?? null },
      crosshair: model.xAxisType === 'datetime',
      categories: model.categories,
      labels: { style: { color: dark ? '#d7e0e8' : '#444' } },
      lineColor: dark ? '#667786' : '#ccd3d8',
      tickColor: dark ? '#667786' : '#ccd3d8',
      ...model.xAxisOptions,
      ...(polarAxes?.xAxis ?? {}),
      ...(discontinuityAxis ?? {})
    };
    if (options.onRangeChange && !model.polar) {
      xAxis.events = {
        afterSetExtremes: (event: { min?: number; max?: number; trigger?: string }) => {
          if (!event.trigger || event.trigger === 'sync' || event.min == null || event.max == null) return;
          options.onRangeChange?.(model.xAxisType === 'datetime'
            ? [Math.round(event.min / 1000), Math.round(event.max / 1000)]
            : [event.min, event.max]);
        }
      };
    }

    const primaryYAxis: Record<string, unknown> = {
      title: model.inverted
        ? { text: model.yAxisTitle ?? null, style: { color: dark ? '#d7e0e8' : '#444' } }
        : highchartsVerticalAxisTitleOptions(model.yAxisTitle ?? null, dark),
      labels: { style: { color: dark ? '#d7e0e8' : '#444' } },
      gridLineColor: dark ? '#334653' : '#e6e6e6',
      ...model.yAxisOptions,
      ...(polarAxes?.yAxis ?? {})
    };
    const secondaryYAxis: Record<string, unknown> = model.secondaryYAxisTitle != null || model.secondaryYAxisOptions
      ? {
          title: highchartsVerticalAxisTitleOptions(model.secondaryYAxisTitle ?? null, dark),
          labels: { style: { color: dark ? '#d7e0e8' : '#444' } },
          opposite: true,
          alignTicks: false,
          gridLineWidth: 0,
          ...model.secondaryYAxisOptions
        }
      : { min: 0, max: 90, alignTicks: false, visible: false, title: { text: null }, labels: { enabled: false }, gridLineWidth: 0 };
    const tooltipTheme = highchartsTooltipTheme(dark);

    const chartOptions: Record<string, unknown> = {
      chart: {
        renderTo: this.container,
        type: model.chartType ?? 'line',
        inverted: model.inverted,
        polar: model.polar,
        animation: false,
        panning: model.xAxisType === 'datetime',
        panKey: 'shift',
        ...(hasSecondaryAxis ? { alignTicks: false } : {}),
        ...(stableEventLegend ? { spacingBottom: 58 } : {}),
        backgroundColor: dark ? '#18232c' : '#ffffff',
        style: { fontFamily: 'Arial, Helvetica, sans-serif' },
        zoomType: options.zoomEnabled === false
          ? undefined
          : !model.polar && ['average-series', 'phase-series', 'harmonic-trend', 'waveform'].includes(options.kind) ? 'x' : undefined,
        resetZoomButton: highchartsResetZoomButtonOptions(dark),
        events: {
          redraw: function (this: HighchartsChart) {
            makeEventMarkersKeyboardAccessible(this);
            bindHighchartsResetZoom(this, options.onResetZoom);
          }
        }
      },
      colors: ['#2f7ed8', '#8bbc21', '#910000', '#492970', '#0d233a', '#1aadce', '#f28f43'],
      title: highchartsTitleOptions(model.title, dark, options.showTitle !== false),
      subtitle: highchartsSubtitleOptions(model.subtitle, dark, options.showSubtitle !== false),
      credits: { enabled: false },
      exporting: {
        enabled: true,
        filename: model.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase(),
        chartOptions: options.showTitle === false || options.showSubtitle === false ? {
          title: options.showTitle === false ? highchartsTitleOptions(model.title, dark) : undefined,
          subtitle: options.showSubtitle === false ? highchartsSubtitleOptions(model.subtitle, dark) : undefined
        } : undefined
      },
      navigation: highchartsNavigationTheme(dark),
      legend: {
        enabled: model.legendEnabled ?? model.series.length > 1,
        ...(stableEventLegend ? {
          floating: true,
          verticalAlign: 'bottom',
          y: 48,
          maxHeight: 46,
          navigation: {
          activeColor: dark ? '#d7e0e8' : '#274b6d',
          inactiveColor: dark ? '#617585' : '#cccccc'
          }
        } : {}),
        itemStyle: { color: dark ? '#e8edf2' : '#333' },
        itemHiddenStyle: { color: dark ? '#7f8d98' : '#ccc' }
      },
      xAxis,
      yAxis: hasSecondaryAxis
        ? [primaryYAxis, secondaryYAxis]
        : primaryYAxis,
      colorAxis: model.colorAxis,
      tooltip: model.polar
        ? { ...tooltipTheme, enabled: model.tooltipEnabled ?? true, shared: false, formatter: polarTooltipFormatter(model.valueSuffix) }
        : model.chartType === 'heatmap'
          ? {
              ...tooltipTheme,
              enabled: model.tooltipEnabled ?? true,
              shared: false,
              formatter: model.heatmapKind === 'time-frequency'
                ? timeFrequencyTooltipFormatter(model.colorValueSuffix)
                : heatmapTooltipFormatter(model.valueSuffix)
            }
          : {
              ...tooltipTheme,
              enabled: model.tooltipEnabled ?? true,
              shared: model.xAxisType === 'datetime' || options.kind === 'waveform',
              formatter: genericTooltipFormatter(model.valueSuffix, model.xAxisType, model.xAxisTitle),
              ...(hasSecondaryAxis && model.xAxisType === 'datetime'
                ? { positioner: eventAwareTrendTooltipPositioner }
                : {})
            },
      plotOptions: {
        series: {
          animation: false,
          turboThreshold: 0,
          states: {
            hover: { lineWidthPlus: 0 }
          },
          ...(model.xAxisType === 'datetime' && (options.onCursorPreview || hasSecondaryAxis) ? {
            point: {
              events: {
                mouseOver: function (this: HighchartsPoint) {
                  const chart = this.series?.chart;
                  const duration = this.duration_ms ?? this.options?.duration_ms;
                  if (chart) {
                    if (typeof duration === 'number') chart._pqEventTooltipPoint = this;
                    else delete chart._pqEventTooltipPoint;
                  }
                  if (typeof this.x === 'number') options.onCursorPreview?.(Math.round(this.x / 1000));
                },
                mouseOut: function (this: HighchartsPoint) {
                  const chart = this.series?.chart;
                  if (chart?._pqEventTooltipPoint === this) delete chart._pqEventTooltipPoint;
                }
              }
            }
          } : {})
        },
        line: { marker: { enabled: false } },
        column: { pointPadding: 0, groupPadding: 0.04, borderWidth: 0 }
      },
      noData: { style: { color: dark ? '#e8edf2' : '#666', fontSize: '14px' } },
      series: model.series.map((series) => series.name === 'Neutral'
        ? { ...series, color: dark ? 'rgba(220, 226, 231, 0.9)' : 'rgba(40, 40, 40, 0.8)' }
        : series)
    };
    this.chart = new highcharts.Chart(chartOptions) as HighchartsChart;
    this.bindMissingTrendHover(trendGaps, options.onCursorPreview);
    this.removeFloatingMenu = installFloatingHighchartsExportMenu(this.chart);
    bindHighchartsResetZoom(this.chart, options.onResetZoom, options.resetZoomVisible === true);
    makeEventMarkersKeyboardAccessible(this.chart);
    if (options.cursor != null) this.setCursor(options.cursor);
  }

  private bindMissingTrendHover(gaps: TrendDataGapRange[], onCursorPreview?: (cursor: number) => void): void {
    const chart = this.chart;
    const target = chart?.container;
    const axis = chart?.xAxis?.[0];
    if (!chart || !target || !axis || gaps.length === 0) return;

    const listener = (event: MouseEvent) => {
      const eventTarget = event.target instanceof Element ? event.target : null;
      if (eventTarget?.closest('[data-pq-event-marker]')) return;
      const bounds = target.getBoundingClientRect();
      const chartX = event.clientX - bounds.left;
      const chartY = event.clientY - bounds.top;
      const plotLeft = chart.plotLeft;
      const plotTop = chart.plotTop;
      const plotWidth = chart.plotWidth;
      const plotHeight = chart.plotHeight;
      if (plotLeft == null || plotTop == null || plotWidth == null || plotHeight == null
        || chartX < plotLeft || chartX > plotLeft + plotWidth
        || chartY < plotTop || chartY > plotTop + plotHeight) return;

      const timestamp = axis.toValue(chartX);
      if (!timestampIsInTrendDataGap(timestamp, gaps)) return;
      chart.pointer?.reset(false, 0);
      onCursorPreview?.(Math.round(timestamp / 1000));
    };
    target.addEventListener('mousemove', listener);
    this.gapMousemoveTarget = target;
    this.gapMousemoveListener = listener;
  }

  setCursor(timestamp: number): void {
    const axis = this.chart?.xAxis?.[0];
    if (!axis) return;
    axis.removePlotLine('workspace-cursor');
    const cursorLine = axis.addPlotLine({ id: 'workspace-cursor', value: timestamp * 1000, color: this.cursorColour, width: 1, zIndex: 5, dashStyle: 'ShortDash' });
    const cursorElement = cursorLine?.svgElem?.element;
    if (cursorElement) cursorElement.style.pointerEvents = 'none';
  }

  setSelectedEvent(timestamp: number | undefined): void {
    if (!this.chart) return;
    const selectedTimestamp = Number.isSafeInteger(timestamp) && (timestamp ?? -1) >= 0
      ? timestamp
      : undefined;
    if (this.selectedEventChart === this.chart && this.selectedEventTimestamp === selectedTimestamp) return;
    const chartChanged = this.selectedEventChart !== this.chart;
    this.selectedEventChart = this.chart;
    this.selectedEventTimestamp = selectedTimestamp;
    if (chartChanged && selectedTimestamp == null) return;

    const markers: HighchartsPoint[] = [];
    for (const series of this.chart.series ?? []) {
      for (const point of series.points ?? []) {
        const duration = point.duration_ms ?? point.options?.duration_ms;
        const pointTimestamp = point.x ?? point.options?.x;
        if (typeof duration !== 'number' || duration <= 0 || typeof pointTimestamp !== 'number') continue;
        markers.push(point);
      }
    }
    const shouldSelect = (point: HighchartsPoint) => selectedTimestamp != null
      && Math.round((point.x ?? point.options?.x ?? Number.NaN) / 1000) === selectedTimestamp;
    // Highcharts 4 shares transient series state. Clear the previous marker
    // before selecting the next one so the new visual cannot be cleared by a
    // later unselect in the same update.
    for (const point of markers) {
      if (point.selected === true && !shouldSelect(point)) point.select?.(false, true);
    }
    for (const point of markers) {
      if (point.selected !== true && shouldSelect(point)) point.select?.(true, true);
    }
    makeEventMarkersKeyboardAccessible(this.chart);
  }

  setRange(range: [number, number]): void {
    const axis = this.chart?.xAxis?.[0];
    if (!axis) return;
    const multiplier = this.xAxisType === 'datetime' ? 1000 : 1;
    const requestedMin = range[0] * multiplier;
    const requestedMax = range[1] * multiplier;
    const current = axis.getExtremes();
    if (typeof current.min === 'number' && typeof current.max === 'number'
      && Math.abs(current.min - requestedMin) <= 0.5
      && Math.abs(current.max - requestedMax) <= 0.5) return;
    axis.setExtremes(requestedMin, requestedMax, true, false, { trigger: 'sync' });
  }

  reflow(): void {
    this.chart?.reflow();
  }

  destroy(): void {
    if (this.gapMousemoveTarget && this.gapMousemoveListener) {
      this.gapMousemoveTarget.removeEventListener('mousemove', this.gapMousemoveListener);
    }
    this.gapMousemoveTarget = null;
    this.gapMousemoveListener = null;
    this.removeFloatingMenu?.();
    this.removeFloatingMenu = null;
    this.chart?.destroy();
    this.chart = null;
    this.selectedEventChart = null;
    this.selectedEventTimestamp = undefined;
    this.container.replaceChildren();
  }
}
